import React, { useState } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

import { AGREEMENT_SIGNER_ROLE, RecipientInfoStatus, SigningMode, SigningOrderParty } from './signing-order.types';
import { DEFAULT_SIGNING_MODE, normalizeSigningParties } from './signing-order.utils';

export interface SigningOrderEditorParty extends SigningOrderParty {}

interface SigningOrderEditorProps {
  parties: SigningOrderEditorParty[];
  onChange: (parties: SigningOrderEditorParty[]) => void;
  signingMode?: SigningMode;
  onSigningModeChange?: (mode: SigningMode) => void;
  getPartySuggestions: (rowIndex: number) => any[];
  searchParties: (event: any) => void;
  onPartyChange: (rowIndex: number, party: any) => void;
  onCreateParty?: (rowIndex: number) => void;
  allowUnknownRecipient?: boolean;
  disabled?: boolean;
}

const roleOptions = [{ label: 'Người ký', value: AGREEMENT_SIGNER_ROLE }];
const recipientInfoStatusOptions: { label: string; value: RecipientInfoStatus }[] = [
  { label: 'Đã biết thông tin người nhận', value: 'KNOWN' },
  { label: 'Chưa biết thông tin người nhận', value: 'UNKNOWN' }
];

const TEMPLATE_SLOT_PREFIX = 'TPL_SLOT_';
const MAX_PARTY_ID_LENGTH = 40;

const getUnknownSlotId = (party: SigningOrderEditorParty, rowIndex: number) => {
  const currentPartyId = String(party.partyId || '');
  if (currentPartyId.startsWith(TEMPLATE_SLOT_PREFIX) && currentPartyId.length <= MAX_PARTY_ID_LENGTH) return currentPartyId;
  const rawId = String(party._internalId || `${Date.now()}-${rowIndex}`).replace(/[^A-Za-z0-9]/g, '').slice(-10);
  return `${TEMPLATE_SLOT_PREFIX}${rowIndex + 1}${rawId ? `_${rawId}` : ''}`.slice(0, MAX_PARTY_ID_LENGTH);
};

const getPartyLabel = (party: SigningOrderEditorParty) => {
  const selectedParty = party.party || {};
  return party.partyName || selectedParty.partyName || selectedParty.label || party.partyId || '';
};

const getEmail = (party: SigningOrderEditorParty) => {
  const selectedParty = party.party || {};
  return party.emailAddress || selectedParty.emailAddress || selectedParty.email || '';
};

const getSelectableSuggestions = (suggestions: any[] = []) => {
  return suggestions.map((suggestion) => {
    const { disabled: _disabled, ...selectableSuggestion } = suggestion || {};
    return selectableSuggestion;
  });
};

const isOrganizationParty = (party: any) => {
  const partyTypeEnumId = String(party?.partyTypeEnumId || '').toLowerCase();
  const partyTypeEnum = String(party?.partyTypeEnum || '').toLowerCase();
  return partyTypeEnumId === 'ptyorganization'
    || partyTypeEnumId.includes('organization')
    || partyTypeEnum.includes('tổ chức')
    || partyTypeEnum.includes('to chuc');
};

const isPersonParty = (party: any) => {
  const partyTypeEnumId = String(party?.partyTypeEnumId || '').toLowerCase();
  const partyTypeEnum = String(party?.partyTypeEnum || '').toLowerCase();
  if (partyTypeEnumId === 'ptyperson'
    || partyTypeEnumId.includes('person')
    || partyTypeEnum.includes('cá nhân')
    || partyTypeEnum.includes('ca nhan')) return true;
  if (isOrganizationParty(party)) return false;
  return !(party?.taxCode || party?.partyTaxId);
};

const getPartyNameFields = (party: any) => {
  const name = party?.partyName || party?.label || '';
  if (isOrganizationParty(party)) {
    return {
      partyName: name,
      representativeName: party?.representativeName || ''
    };
  }

  return {
    partyName: name,
    representativeName: ''
  };
};

const sequenceColors = ['#7dd3fc', '#fdba74', '#fcd34d', '#86efac', '#c4b5fd', '#f9a8d4'];

export const SigningOrderEditor: React.FC<SigningOrderEditorProps> = ({
  parties,
  onChange,
  signingMode = DEFAULT_SIGNING_MODE,
  onSigningModeChange = () => {},
  getPartySuggestions,
  searchParties,
  onPartyChange,
  onCreateParty,
  allowUnknownRecipient = false,
  disabled = false
}) => {
  const [draggingSequence, setDraggingSequence] = useState<number | null>(null);
  const [dragOverSequence, setDragOverSequence] = useState<number | null>(null);
  const normalizedParties = normalizeSigningParties(parties || []) as SigningOrderEditorParty[];
  const isSequential = signingMode !== 'AgtSignParallel';

  const emitChange = (nextParties: SigningOrderEditorParty[]) => {
    onChange(normalizeSigningParties(nextParties) as SigningOrderEditorParty[]);
  };

  const patchParty = (index: number, patch: Partial<SigningOrderEditorParty>) => {
    const nextParties = [...normalizedParties];
    nextParties[index] = { ...nextParties[index], ...patch };
    emitChange(nextParties);
  };

  const addSigner = () => {
    emitChange([
      ...normalizedParties,
      {
        _internalId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        roleTypeId: AGREEMENT_SIGNER_ROLE,
        recipientInfoStatus: 'KNOWN',
        sequenceNum: normalizedParties.length + 1,
        signingOrder: normalizedParties.length + 1,
        emailAddress: ''
      }
    ]);
  };

  const addSameOrderSigner = (sequenceNum: number) => {
    emitChange([
      ...normalizedParties,
      {
        _internalId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        roleTypeId: AGREEMENT_SIGNER_ROLE,
        recipientInfoStatus: 'KNOWN',
        sequenceNum,
        signingOrder: sequenceNum,
        emailAddress: ''
      }
    ]);
  };

  const removeSigner = (index: number) => {
    emitChange(normalizedParties.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleModeChange = (checked: boolean) => {
    onSigningModeChange(checked ? 'AgtSignSequential' : 'AgtSignParallel');
    emitChange(normalizedParties.map((party, index) => {
      const sequenceNum = checked ? index + 1 : 1;
      return {
        ...party,
        sequenceNum,
        signingOrder: sequenceNum
      };
    }));
  };

  const handlePartySelected = (rowIndex: number, selectedParty: any) => {
    if (!selectedParty) return;
    const partyNameFields = getPartyNameFields(selectedParty);
    const selectedIsPerson = isPersonParty(selectedParty);

    patchParty(rowIndex, {
      party: selectedParty,
      partyId: selectedParty.partyId || selectedParty.value || '',
      partyName: partyNameFields.partyName,
      emailAddress: selectedParty.emailAddress || selectedParty.email || '',
      taxCode: selectedIsPerson ? '' : (selectedParty.taxCode || selectedParty.partyTaxId || ''),
      contactNumber: selectedParty.contactNumber || selectedParty.phoneNumber || '',
      representativeName: partyNameFields.representativeName,
      identityCard: selectedParty.identityCard || selectedParty.idValue || '',
      partyTypeEnumId: selectedParty.partyTypeEnumId || (selectedIsPerson ? 'PtyPerson' : 'PtyOrganization'),
      recipientInfoStatus: 'KNOWN',
      roleTypeId: AGREEMENT_SIGNER_ROLE
    });
    onPartyChange(rowIndex, selectedParty);
  };

  const handleRecipientInfoStatusChange = (rowIndex: number, status: RecipientInfoStatus) => {
    const party = normalizedParties[rowIndex];
    if (!party) return;

    if (status === 'UNKNOWN') {
      patchParty(rowIndex, {
        recipientInfoStatus: 'UNKNOWN',
        party: null,
        partyId: getUnknownSlotId(party, rowIndex),
        partyName: '',
        emailAddress: '',
        taxCode: '',
        contactNumber: '',
        representativeName: '',
        identityCard: '',
        partyTypeEnumId: undefined,
        roleTypeId: AGREEMENT_SIGNER_ROLE
      });
      return;
    }

    patchParty(rowIndex, {
      recipientInfoStatus: 'KNOWN',
      party: null,
      partyId: '',
      partyName: '',
      emailAddress: '',
      taxCode: '',
      contactNumber: '',
      representativeName: '',
      identityCard: '',
      partyTypeEnumId: undefined,
      roleTypeId: AGREEMENT_SIGNER_ROLE
    });
  };

  const sequentialGroups = normalizedParties.reduce<Record<number, SigningOrderEditorParty[]>>((acc, party) => {
    const sequenceNum = party.sequenceNum || 1;
    acc[sequenceNum] = acc[sequenceNum] || [];
    acc[sequenceNum].push(party);
    return acc;
  }, {});
  const groups: Record<number, SigningOrderEditorParty[]> = isSequential
    ? sequentialGroups
    : (normalizedParties.length ? { 1: normalizedParties } : {});
  const groupNumbers = isSequential
    ? Object.keys(groups).map(Number).sort((a, b) => a - b)
    : (normalizedParties.length ? [1] : []);

  const reorderGroup = (sourceSequence: number, targetSequence: number) => {
    if (sourceSequence === targetSequence) return;

    const nextGroupNumbers = [...groupNumbers];
    const sourceIndex = nextGroupNumbers.indexOf(sourceSequence);
    const targetIndex = nextGroupNumbers.indexOf(targetSequence);
    if (sourceIndex < 0 || targetIndex < 0) return;

    nextGroupNumbers.splice(sourceIndex, 1);
    nextGroupNumbers.splice(targetIndex, 0, sourceSequence);

    const sequenceMap = new Map<number, number>();
    nextGroupNumbers.forEach((sequenceNum, index) => sequenceMap.set(sequenceNum, index + 1));

    emitChange(normalizedParties.map((party) => {
      const nextSequenceNum = sequenceMap.get(party.sequenceNum || 1) || party.sequenceNum || 1;
      return {
        ...party,
        sequenceNum: nextSequenceNum,
        signingOrder: nextSequenceNum
      };
    }));
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex align-items-center justify-content-between">
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="signingModeSequential"
            checked={isSequential}
            onChange={(event) => handleModeChange(!!event.checked)}
            disabled={disabled}
          />
          <label htmlFor="signingModeSequential">Thiết lập ký theo thứ tự</label>
          {!isSequential && <span className="text-500 text-sm">Người ký sẽ nhận thông báo song song.</span>}
        </div>
        {!disabled && (
          <Button label="Thêm người ký" icon="pi pi-plus" text onClick={addSigner} />
        )}
      </div>

      {groupNumbers.map((sequenceNum) => {
        const isDragging = draggingSequence === sequenceNum;
        const isDropTarget = dragOverSequence === sequenceNum && draggingSequence !== sequenceNum;
        const sequenceColor = sequenceColors[(sequenceNum - 1) % sequenceColors.length];

        return (
          <div
            key={sequenceNum}
            className="flex gap-3"
            draggable={!disabled && isSequential}
            onDragStart={(event) => {
              if (!isSequential) return;
              setDraggingSequence(sequenceNum);
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', String(sequenceNum));
            }}
            onDragOver={(event) => {
              if (disabled || !isSequential) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setDragOverSequence(sequenceNum);
            }}
            onDragLeave={() => isSequential && setDragOverSequence(null)}
            onDrop={(event) => {
              event.preventDefault();
              if (!isSequential) return;
              const sourceSequence = Number(event.dataTransfer.getData('text/plain') || draggingSequence);
              setDraggingSequence(null);
              setDragOverSequence(null);
              if (sourceSequence) reorderGroup(sourceSequence, sequenceNum);
            }}
            onDragEnd={() => {
              setDraggingSequence(null);
              setDragOverSequence(null);
            }}
            style={{
              opacity: isDragging ? 0.55 : 1,
              outline: isDropTarget ? '2px dashed var(--primary-color)' : 'none',
              outlineOffset: isDropTarget ? '4px' : 0,
              borderRadius: 8
            }}
          >
            {isSequential && (
              <div className="flex flex-column align-items-center pt-4" style={{ width: 48 }}>
                <span className="border-1 border-round px-3 py-2 bg-white">{sequenceNum}</span>
                <i className="pi pi-bars mt-2 text-500" style={{ cursor: disabled ? 'default' : 'grab' }} />
              </div>
            )}
            <div className="flex-1 surface-50 border-round p-3">
              {groups[sequenceNum].map((party) => {
                const rowIndex = normalizedParties.findIndex(item => item._internalId === party._internalId);
                const isPerson = isPersonParty(party) || isPersonParty(party.party);
                const recipientInfoStatus = allowUnknownRecipient ? (party.recipientInfoStatus || 'KNOWN') : 'KNOWN';
                const isUnknownRecipient = allowUnknownRecipient && recipientInfoStatus === 'UNKNOWN';
                return (
                  <div key={party._internalId || rowIndex} className="flex align-items-stretch gap-3 mb-3">
                    <div className="border-round" style={{ width: 8, backgroundColor: sequenceColor }} />
                    <div className="surface-0 border-round p-3 flex-1">
                      <div className="grid align-items-end">
                        {allowUnknownRecipient && (
                          <div className="col-12">
                            <Dropdown
                              value={recipientInfoStatus}
                              options={recipientInfoStatusOptions}
                              disabled={disabled}
                              onChange={(event) => handleRecipientInfoStatusChange(rowIndex, event.value)}
                              className="w-full md:w-auto"
                              style={{ border: 0, boxShadow: 'none', background: 'transparent' }}
                            />
                          </div>
                        )}
                        {isUnknownRecipient ? (
                          <div className="col-12 md:col-4">
                            <label className="font-medium block mb-2">Tên vị trí <span className="text-red-500">*</span></label>
                            <InputText
                              value={party.partyName || ''}
                              disabled={disabled}
                              placeholder="Thêm vị trí (Bên A, Giám đốc,...)"
                              onChange={(event) => patchParty(rowIndex, { partyName: event.target.value })}
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="col-12 md:col-4">
                              <label className="font-medium block mb-2">Đối tượng <span className="text-red-500">*</span></label>
                              <AutoComplete
                                value={party.party || getPartyLabel(party)}
                                suggestions={getSelectableSuggestions(getPartySuggestions(rowIndex))}
                                completeMethod={searchParties}
                                field="label"
                                dropdown
                                disabled={disabled}
                                placeholder="Tìm kiếm đối tượng"
                                className="w-full"
                                inputClassName="w-full"
                                onChange={(event) => {
                                  if (typeof event.value === 'string') {
                                    patchParty(rowIndex, { partyName: event.value, party: null, partyId: '', recipientInfoStatus: 'KNOWN' });
                                    return;
                                  }
                                  handlePartySelected(rowIndex, event.value);
                                }}
                                onSelect={(event) => handlePartySelected(rowIndex, event.value)}
                              />
                            </div>
                            <div className="col-12 md:col-3">
                              <label className="font-medium block mb-2">Họ và Tên</label>
                              <InputText
                                value={isPerson ? (party.partyName || '') : (party.representativeName || '')}
                                disabled={disabled}
                                onChange={(event) => patchParty(rowIndex, isPerson ? { partyName: event.target.value } : { representativeName: event.target.value })}
                                className="w-full"
                              />
                            </div>
                            <div className="col-12 md:col-3">
                              <label className="font-medium block mb-2">Email <span className="text-red-500">*</span></label>
                              <InputText
                                value={getEmail(party)}
                                disabled={disabled}
                                onChange={(event) => patchParty(rowIndex, { emailAddress: event.target.value })}
                                className="w-full"
                              />
                            </div>
                            <div className="col-12 md:col-2">
                              <label className="font-medium block mb-2">Số điện thoại</label>
                              <InputText
                                value={party.contactNumber || ''}
                                disabled={disabled}
                                onChange={(event) => patchParty(rowIndex, { contactNumber: event.target.value })}
                                className="w-full"
                              />
                            </div>
                            {!isPerson && (
                              <div className="col-12 md:col-3">
                                <label className="font-medium block mb-2">Tên công ty</label>
                                <InputText
                                  value={party.partyName || ''}
                                  disabled={disabled}
                                  onChange={(event) => patchParty(rowIndex, { partyName: event.target.value })}
                                  className="w-full"
                                />
                              </div>
                            )}
                            <div className="col-12 md:col-3">
                              <label className="font-medium block mb-2">CCCD/CMND</label>
                              <InputText
                                value={party.identityCard || ''}
                                disabled={disabled}
                                onChange={(event) => patchParty(rowIndex, { identityCard: event.target.value })}
                                className="w-full"
                              />
                            </div>
                          </>
                        )}
                        <div className="col-12 md:col-3">
                          <label className="font-medium block mb-2">Vai trò</label>
                          <Dropdown value={AGREEMENT_SIGNER_ROLE} options={roleOptions} disabled className="w-full" />
                        </div>
                      </div>
                      {!disabled && onCreateParty && !isUnknownRecipient && !party.partyId && (
                        <Button label="Tạo đối tượng mới" icon="pi pi-user-plus" text className="mt-2" onClick={() => onCreateParty(rowIndex)} />
                      )}
                    </div>
                    <div className="flex align-items-center gap-3">
                      <Button icon="pi pi-trash" text severity="danger" disabled={disabled} onClick={() => removeSigner(rowIndex)} />
                      {isSequential && <i className="pi pi-bars text-500" style={{ cursor: disabled ? 'default' : 'grab', fontSize: '1.25rem' }} />}
                    </div>
                  </div>
                );
              })}
              {!disabled && isSequential && (
                <div className="flex justify-content-end">
                  <Button
                    label="Thêm người nhận cùng số thứ tự"
                    icon="pi pi-plus"
                    text
                    onClick={() => addSameOrderSigner(sequenceNum)}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {!normalizedParties.length && (
        <div className="surface-50 border-round p-4 text-center text-600">
          Chưa có người ký. Bấm "Thêm người ký" để cấu hình luồng ký.
        </div>
      )}
    </div>
  );
};
